import { useCallback, useEffect, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { useConfirm } from '@/components/ui/ConfirmDialog';
import { notify } from '@/core/toast/notify';
import { projectsApi } from '@/api/projects.api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Modal } from '@/components/ui/Modal';
import { useDebounce } from '@/hooks/useDebounce';
import { usePagination } from '@/hooks/usePagination';
import { useProjectList } from '@/modules/projects/shared/useProjectList';
import type { ProjectTask } from '@/types/projects';
import { showApiError } from '@/utils/apiError';

export function ProjectsTasksPage() {
  const { confirm, dialog } = useConfirm();
  const pagination = usePagination();
  const debouncedSearch = useDebounce(pagination.search);
  const { projects } = useProjectList();
  const [projectId, setProjectId] = useState<number | undefined>();
  const [data, setData] = useState<ProjectTask[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [editTask, setEditTask] = useState<ProjectTask | null>(null);
  const [form, setForm] = useState({ title: '', taskType: 'TASK' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await projectsApi.listTasks(projectId, {
        page: pagination.page,
        size: pagination.size,
        search: debouncedSearch || undefined,
      });
      setData(res.content);
      pagination.applyPageResponse(res);
    } catch (err) {
      showApiError(err, 'Failed to load tasks');
    } finally {
      setLoading(false);
    }
  }, [pagination, debouncedSearch, projectId]);

  useEffect(() => {
    void load();
  }, [load]);

  const columns: Column<ProjectTask>[] = [
    { key: 'title', header: 'Title' },
    { key: 'taskType', header: 'Type' },
    { key: 'projectId', header: 'Project' },
    { key: 'dueDate', header: 'Due' },
    { key: 'estimatedHours', header: 'Est. hours' },
    {
      key: 'id',
      header: '',
      render: (row) => (
        <div className="flex gap-1">
          <button
            type="button"
            className="p-1 text-slate-500 hover:text-primary-600"
            title="Edit"
            onClick={() => {
              setEditTask(row);
              setForm({ title: row.title, taskType: row.taskType ?? 'TASK' });
              setOpen(true);
            }}
          >
            <EditOutlinedIcon fontSize="small" />
          </button>
          <button
            type="button"
            className="p-1 text-slate-500 hover:text-red-600"
            title="Delete"
            onClick={() => void remove(row.id)}
          >
            <DeleteOutlineIcon fontSize="small" />
          </button>
        </div>
      ),
    },
  ];

  const create = async () => {
    if (!projectId) {
      notify.error('Select a project');
      return;
    }
    try {
      if (editTask) {
        await projectsApi.updateTask(editTask.id, { title: form.title, taskType: form.taskType });
        notify.success('Task updated');
      } else {
        await projectsApi.createTask({ projectId, title: form.title, taskType: form.taskType });
        notify.success('Task created');
      }
      setOpen(false);
      setEditTask(null);
      load();
    } catch (err) {
      showApiError(err, editTask ? 'Update failed' : 'Create failed');
    }
  };

  const remove = async (id: number) => {
    const ok = await confirm({
      title: 'Delete task',
      message: 'This task will be permanently removed.',
      confirmLabel: 'Delete',
      variant: 'danger',
    });
    if (!ok) return;
    try {
      await projectsApi.deleteTask(id);
      notify.success('Task deleted');
      load();
    } catch (err) {
      showApiError(err, 'Delete failed');
    }
  };

  return (
    <div>
      <PageHeader
        title="Tasks"
        description="Hierarchical tasks with create, edit, and delete"
        breadcrumbs={[{ label: 'Org', to: '/org' }, { label: 'Projects', to: '/projects' }, { label: 'Tasks' }]}
        actions={
          <Button
            onClick={() => {
              setEditTask(null);
              setForm({ title: '', taskType: 'TASK' });
              setOpen(true);
            }}
            disabled={!projectId}
          >
            <AddIcon fontSize="small" /> New task
          </Button>
        }
      />
      <div className="mb-4">
        <Select
          placeholder="All projects"
          value={String(projectId ?? '')}
          onChange={(v) => setProjectId(v ? Number(v) : undefined)}
          options={projects.map((p) => ({ value: String(p.id), label: p.projectName }))}
        />
      </div>
      <DataTable columns={columns}
        data={data}
        keyField="id"
        loading={loading}
        search={pagination.search}
        onSearchChange={(v) => {
          pagination.setSearch(v);
          pagination.resetPage();
        }}
        page={pagination.page}
        totalPages={pagination.totalPages}
        totalElements={pagination.totalElements}
        onPageChange={pagination.setPage}
      />
      <Modal
        open={open}
        onClose={() => {
          setOpen(false);
          setEditTask(null);
        }}
        title={editTask ? 'Edit task' : 'Create task'}
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => {
                setOpen(false);
                setEditTask(null);
              }}
            >
              Cancel
            </Button>
            <Button onClick={() => void create()}>Save</Button>
          </>
        }
      >
        <Input label="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
      </Modal>
      {dialog}
    </div>
  );
}
